import React, { useEffect, useState } from "react";
import { Container as ContainerBase } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Dialog } from 'primereact/dialog';
import { Editor } from "primereact/editor";
import { createBlog, updateBlog } from "api/blog";

// Tailwind-styled containers
const Container = tw(ContainerBase)`min-h-screen bg-gray-100 text-gray-900 font-medium flex justify-center items-center`;
const Content = tw.div`bg-white rounded-2xl shadow-lg w-full max-w-3xl p-8`;
const Heading = tw.h1`text-3xl font-bold mb-6 text-center text-pbsGreen-900`;
const Form = tw.form`space-y-5`;
const Input = tw.input`w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-pbsGreen-500 focus:bg-white`;
const Label = tw.label`block text-sm font-semibold text-gray-700`;
const Field = tw.div`space-y-2`;
const ErrorMessage = tw.p`text-red-600 text-sm text-center`;

const SubmitButton = styled.button`
  ${tw`w-full py-3 rounded-md bg-pbsGreen-500 text-white font-semibold text-sm hover:bg-pbsGreen-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed`}
`;

const emptyForm = { title: '', category: '', imageUrl: '', text: '' };

export default function PostFormModal({ visible, setVisible, post = null, onSaved }) {
  const isEditMode = Boolean(post && post.id);

  const [title, setTitle] = useState(emptyForm.title);
  const [category, setCategory] = useState(emptyForm.category);
  const [imageUrl, setImageUrl] = useState(emptyForm.imageUrl);
  const [text, setText] = useState(emptyForm.text);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Populate the form when opening in edit mode, or reset it when opening in create mode
  useEffect(() => {
    if (!visible) return;

    setErrorMessage('');

    if (isEditMode) {
      setTitle(post.title || '');
      setCategory(post.category || '');
      setImageUrl(post.image_url || '');
      setText(post.post || post.description || '');
    } else {
      setTitle(emptyForm.title);
      setCategory(emptyForm.category);
      setImageUrl(emptyForm.imageUrl);
      setText(emptyForm.text);
    }
  }, [visible, post, isEditMode]);

  const handleClose = () => {
    if (isSubmitting) return;
    setVisible(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    const payload = { title, category, image_url: imageUrl, post: text };
    const request = isEditMode
      ? updateBlog({ id: post.id, ...payload })
      : createBlog(payload);

    request
      .then((response) => {
        console.log(isEditMode ? 'Post updated successfully:' : 'Post created successfully:', response);
        setVisible(false);
        if (onSaved) onSaved();
      })
      .catch((error) => {
        console.error(isEditMode ? 'Error updating post:' : 'Error creating post:', error);
        setErrorMessage(
          isEditMode
            ? 'Failed to update the post. Please try again.'
            : 'Failed to create the post. Please try again.'
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Dialog
      visible={visible}
      modal
      dismissableMask
      onHide={handleClose}
      breakpoints={{ '960px': '90vw' }}
      style={{ width: '50vw' }}
      contentStyle={{ padding: 0, borderRadius: '1rem', maxHeight: '90vh', overflowY: 'auto' }}
      maskClassName="bg-black bg-opacity-50"
    >
      <div className="overflow-y-auto max-h-[85vh]">
        <Container>
          <Content>
            <Heading>{isEditMode ? 'Edit Job Post' : 'Create Job Post'}</Heading>
            <Form onSubmit={handleSubmit}>
              <Field>
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  type="text"
                  name="image_url"
                  id="image_url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="title">Title</Label>
                <Input
                  type="text"
                  name="title"
                  id="title"
                  placeholder="Job Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="category">Category</Label>
                <Input
                  type="text"
                  name="category"
                  id="category"
                  placeholder="Development, Marketing, etc."
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="description">Description</Label>
                <Editor
                  value={text}
                  onTextChange={(e) => setText(e.htmlValue)}
                  style={{ height: '200px' }}
                />
              </Field>

              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (isEditMode ? 'Saving...' : 'Submitting...') : (isEditMode ? 'Save Changes' : 'Submit')}
              </SubmitButton>
            </Form>
          </Content>
        </Container>
      </div>
    </Dialog>
  );
}
